/**
 * NotificationsByTransfers
 *
 * Shared component for displaying notifications by transaction hash.
 * Used by cashier and manager features.
 */
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { getNotificationStatusLabel, NOTIFICATION_STATES } from '@constants/index';
import Dimmer from '@shared/components/ui/Dimmer';
import { getNotificationsByHash } from '@shared/services/api/notifications';
import { getLabel } from '@shared/utils';
import map from 'lodash/map';

import { formatDateTimeDash } from '@shared/utils/dateUtils';

function getStatusColor(status) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'success' || normalized === NOTIFICATION_STATES.success.toLowerCase()) return 'success';
  if (normalized === 'failed' || normalized === NOTIFICATION_STATES.failed.toLowerCase()) return 'error';
  return 'default';
}

function DataRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ py: 0.5 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.78rem', flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: 500, fontSize: '0.85rem', textAlign: 'right', wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Stack>
  );
}

DataRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
};

function NotificationCard({ notification }) {
  const theme = useTheme();
  const statusColor = getStatusColor(notification.status);

  return (
    <Paper
      elevation={0}
      data-testid="notification-card"
      sx={{
        borderRadius: '14px',
        overflow: 'hidden',
        flexShrink: 0,
        bgcolor: 'grey.100',
        boxShadow: '0 1px 8px rgba(0,0,0,0.05)',
      }}>
      <Box sx={{ height: 3, backgroundColor: statusColor === 'default' ? 'primary.main' : `${statusColor}.main` }} />
      <Box sx={{ px: 2, pt: 1.5, pb: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.85rem' }}>
            {notification.type}
          </Typography>
          <Chip
            label={notification.status}
            color={statusColor === 'default' ? undefined : statusColor}
            size="small"
            sx={{ fontWeight: 600, fontSize: '0.7rem', height: 24, borderRadius: '8px' }}
          />
        </Stack>
        <Divider sx={{ my: 0.8, borderColor: alpha(theme.palette.divider, 0.5) }} />
        <DataRow label={getLabel('date')} value={notification.date} />
        <DataRow label={getLabel('destiny')} value={notification.destiny} />
        <Divider sx={{ my: 0.8, borderColor: alpha(theme.palette.divider, 0.5) }} />
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
          {getLabel('content')}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: '0.85rem', mt: 0.4, wordBreak: 'break-word' }}>
          {notification.content}
        </Typography>
      </Box>
    </Paper>
  );
}

NotificationCard.propTypes = {
  notification: PropTypes.shape({
    content: PropTypes.string,
    date: PropTypes.string,
    destiny: PropTypes.string,
    type: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
};

function Row(props) {
  const { row } = props;
  const [open, setOpen] = React.useState(false);

  return (
    <React.Fragment>
      <TableRow>
        <TableCell align="left">{row.date}</TableCell>
        <TableCell align="left">{row.type}</TableCell>
        <TableCell align="left">{row.destiny}</TableCell>
        <TableCell align="left">{row.status}</TableCell>
        <TableCell>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={1}>
              <Typography variant="h6" gutterBottom component="div">
                {getLabel('content')}
              </Typography>
              <Typography gutterBottom component="div">
                {row.content}
              </Typography>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

Row.propTypes = {
  row: PropTypes.shape({
    content: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
    destiny: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
  }).isRequired,
};

const NotificationsByTransfer = ({ hashId = '' }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up(768));
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNotificationsData = async () => {
      try {
        setLoading(true);
        const fetchNotifications = await getNotificationsByHash(hashId);
        const { data = [] } = fetchNotifications || {};
        const buildData = map(data, (transaction) => ({
          ...transaction,
          date: formatDateTimeDash(transaction.date),
          status: getNotificationStatusLabel(transaction.status),
        }));
        setNotifications(buildData);
      } catch (error) {
        console.error('Error fetching notifications:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNotificationsData();
  }, [hashId]);

  return (
    <Dimmer active={loading}>
      {isDesktop ? (
        <Table aria-label="collapsible table">
          <TableHead>
            <TableRow>
              <TableCell align="left">{getLabel('date')}</TableCell>
              <TableCell align="left">{getLabel('type')}</TableCell>
              <TableCell align="left">{getLabel('destiny')}</TableCell>
              <TableCell align="left">{getLabel('state')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {notifications.map((notification) => (
              <Row key={notification.date} row={notification} />
            ))}
          </TableBody>
        </Table>
      ) : (
        <Box
          data-testid="notifications-cards"
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
            maxHeight: 'calc(100dvh - 64px)',
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            pr: 0.5,
          }}>
          <Stack spacing={1.5} sx={{ pb: 1 }}>
            {notifications.map((notification) => (
              <NotificationCard key={`${notification.date}-${notification.destiny}`} notification={notification} />
            ))}
          </Stack>
        </Box>
      )}
    </Dimmer>
  );
};

NotificationsByTransfer.propTypes = {
  hashId: PropTypes.string,
};

export default NotificationsByTransfer;
